import { UniAgFns } from "@/shared/uni-ag-fns";
import { GetRowIdParams, GridReadyEvent, RowSelectedEvent, ValueGetterParams } from "@ag-grid-community/core";
import { Location } from "@angular/common";
import { Component, OnInit, Optional, TemplateRef, ViewChild, ViewEncapsulation } from "@angular/core";
import { InventoryManagerComponent } from "./inventory-manager.component";

import { ActivatedRoute } from "@angular/router";
import {
  ProductCrosstabSummary,
  ProductSummary,
  ProductTypeAccountCrosstabSummary,
  ProductTypeAccountSummary,
  ProductTypeXXXCrosstabSummary,
  ProductTypeXXXSummary,
} from "app/model/entities/-product-type-summary";
import { EOQType, Product, ProductEOQ, ProductType } from "app/model/entities/entity-model";
import { EOQTypeCode } from "app/model/enums/eoq-type-code";
import { AgFns, GridOptionsExt, ISortModel } from "app/shared/ag-fns";
import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { UtilFns } from "app/shared/util-fns";
import { EntityState } from "breeze-client";
import _ from "lodash";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "inventory-eoq",
  templateUrl: "./inventory-eoq.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class InventoryEoqComponent extends DomainBaseComponent implements OnInit {
  @ViewChild("ptButtonsCell") ptButtonsCell: TemplateRef<any>;

  ptGridOptions: GridOptionsExt;
  ptaGridOptions: GridOptionsExt;

  prodGridOptions: GridOptionsExt;
  showPercentages = false;

  currentProductType: ProductType;
  currentProductsForEOQ: Product[]; // products restricted to just reorderable ones
  eOQTypes: EOQType[];

  ptaItems: ProductTypeAccountCrosstabSummary[];
  ptaTotals: ProductTypeAccountCrosstabSummary[]; // just one row

  prodItems: ProductCrosstabSummary[];
  prodTotals: ProductCrosstabSummary[]; // just one row

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    protected location: Location,
    @Optional() private inventoryManager: InventoryManagerComponent
  ) {
    super(domainService);

    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });

    // Subscribe to show formula command from parent button
    this.inventoryManager?.showFormulaClicked$.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.showFormula();
    });

    // Subscribe to show percentages checkbox changes from parent
    this.inventoryManager?.showPercentagesChanged$.pipe(takeUntil(this.onDestroy)).subscribe((value) => {
      this.showPercentages = value;
      this.showHideProdColumns();
    });

    // Show the EOQ buttons when this page is active (deferred to avoid ExpressionChangedAfterItHasBeenCheckedError)
    if (this.inventoryManager) {
      setTimeout(() => {
        // Reset other pages' controls
        this.inventoryManager.showScannerControls = false;
        this.inventoryManager.showAddInventoryButtons = false;
        this.inventoryManager.showPrintBarcodesButton = false;
        // Show this page's controls
        this.inventoryManager.showEoqButtons = true;
      });
    }
  }

  override ngOnDestroy() {
    // Hide the EOQ buttons and reset state when leaving this page
    if (this.inventoryManager) {
      this.inventoryManager.showEoqButtons = false;
      this.inventoryManager.canShowFormula = false;
      this.inventoryManager.showPercentages = false;
    }
    super.ngOnDestroy();
  }

  async updateFromContext() {
    // this.gridState = AgFns.createGridState(this.route.snapshot.queryParams, 'id');
    this.eOQTypes = this.dbQueryService.getAllCached(EOQType);

    this.ptGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPtGridReady,
      onRowSelected: this.onPtRowSelected,
      onFilterChanged: this.onFilterChanged,
      onCellValueChanged: this.onProdCellValueChanged, // note NOT ptCellValueChanged
      rowModelType: "serverSide",
      onPaginationChanged: (params) => {
        if (params.newPage) {
          this.updateLocation();
        }
      },
    });
    AgFns.captureGridRouteParams(this.ptGridOptions, this.route, "id");

    this.ptaGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPtaGridReady,
      getRowId: this.getPtaRowId,
      getRowStyle: this.getPtaRowStyle,
    });

    this.prodGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onProdGridReady,
      onCellValueChanged: this.onProdCellValueChanged,
      getRowId: this.getProdRowId,
      getRowStyle: this.getPtaRowStyle, // note same style as above
    });

    this.isPageReady = true;
  }

  async canDeactivate() {
    this.stopEditing();
    const ok = await super.canDeactivate();
    if (!ok) return false;

    if (this.uow.hasChanges()) {
      const ok = await this.dialogService.yesNoDialog("Changes have not been saved", "Do you want to save before exiting?");
      if (ok) {
        await this.dbSaveService.saveChanges();
      } else {
        this.dbSaveService.rejectChanges();
      }
    }

    this.uow.clearEntities(ProductType);
    return true;
  }

  async onPtGridReady(event: GridReadyEvent) {
    const cellClass = this.user.isInventoryAdmin ? "cell-editable" : "";
    const colDefs = [
      { ...(AgFns.createCellButtonProps("", this.ptButtonsCell) as any), ...AgFns.fixWidth(140) },
      { ...UniAgFns.getManufacturerColDef("manufacturer.name") },
      { ...UniAgFns.getProductTypeSKUColDef("style") },
      { ...UniAgFns.getProductTypeDescriptionColDef("description") },

      {
        headerName: "EOQ Type",
        field: "eOQTypeId",
        filter: "agTextColumnFilter",
        editable: true,
        cellClass: cellClass,
        ...AgFns.createDropdownEditorPropsFromArray(this.eOQTypes, "id", "name"),
        ...AgFns.fixWidth(160),
      },
      {
        headerName: "Lead Time (days)",
        field: "leadTimeDays",
        editable: true,
        cellClass: "cell-editable",
        filter: "agNumberColumnFilter",
        ...AgFns.fixWidth(160),
      },
      {
        headerName: "Ordering Cost",
        field: "orderingCostAmt",
        editable: true,
        cellClass: "cell-editable",
        filter: "agNumberColumnFilter",
        ...AgFns.fixWidth("money"),
      },
      {
        headerName: "Unit Carrying Cost/Year",
        field: "unitCarryingCostPerYearAmt",
        editable: true,
        cellClass: "cell-editable",
        filter: "agNumberColumnFilter",
        ...AgFns.fixWidth(200),
      },
      {
        headerName: "Safety Stock/ Year Pct",
        field: "safetyStockPerYearPct",
        editable: true,
        cellClass: "cell-editable",
        filter: "agNumberColumnFilter",
        ...AgFns.fixWidth(200),
      },
      // { headerName: 'Product Type Id', field: 'id', filter: 'agNumberColumnFilter' },
      { headerName: "Last modified", field: "eOQModTs", ...AgFns.fixWidth("ts") },
    ];
    /*     colDefs.push({ ...AgFns.createCellButtonProps('', this.ptButtonsCell) as any }); */
    const sortModel = [
      { colId: "manufacturer.name", sort: "asc" },
      { colId: "style", sort: "asc" },
      { colId: "description", sort: "asc" },
    ] as ISortModel;

    AgFns.initGrid(event, colDefs, sortModel);

    this.updateDatasource();
    AgFns.applyGridRouteParams(this.ptGridOptions);
  }

  async onPtaGridReady(event: GridReadyEvent) {
    const currYear = new Date().getFullYear();
    const colDefs = [
      // { headerName: 'Manufacturer', field: 'manufacturer.name', filter: 'agTextColumnFilter' },
      // { headerName: 'Style', field: 'productType.style', filter: 'agTextColumnFilter' },
      // { headerName: 'Description', field: 'productType.description', filter: 'agTextColumnFilter' },
      {
        headerName: "Account",
        valueGetter: (params: ValueGetterParams) => {
          if (params.node?.isRowPinned()) {
            return "Total";
          }
          const x = <ProductTypeAccountCrosstabSummary>params.data;
          return (x && x.account?.accountName) ?? "[None]";
        },
        ...AgFns.flexWidth(200),
      },
      {
        headerName: `Qty-${currYear}-YTD`,
        field: "year0Qty",
        valueFormatter: (p) => p.value || "",
        ...AgFns.fixWidth(140),
      },
      {
        headerName: `Qty-${currYear - 1}`,
        field: "year1Qty",
        valueFormatter: (p) => p.value || "",
        ...AgFns.fixWidth(120),
      },
      {
        headerName: `Qty-${currYear - 2}`,
        field: "year2Qty",
        valueFormatter: (p) => p.value || "",
        ...AgFns.fixWidth(120),
      },
      {
        headerName: `Qty-${currYear - 3}`,
        field: "year3Qty",
        valueFormatter: (p) => p.value || "",
        ...AgFns.fixWidth(120),
      },
      {
        headerName: `Qty-Inventory`,
        field: "inventoryQty",
        valueFormatter: (p) => p.value || "",
        ...AgFns.fixWidth(120),
      },
    ];
    const sortModel = [{ colId: "account.accountName", sort: "asc" as const }];

    AgFns.initGrid(event, colDefs, sortModel);
  }

  async onProdGridReady(event: GridReadyEvent) {
    const currYear = new Date().getFullYear();
    const colDefs = [
      // { headerName: 'Manufacturer', field: 'manufacturer.name', filter: 'agTextColumnFilter' },
      // { headerName: 'Style', field: 'productType.style', filter: 'agTextColumnFilter' },
      // { headerName: 'Description', field: 'productType.description', filter: 'agTextColumnFilter' },
      // { headerName: 'ProductId', field: 'productId',  },
      {
        headerName: "Features",
        field: "product.featureChoicesExtract",
        ...AgFns.flexWidth(200),
      },
      {
        headerName: `Qty-${currYear}-YTD`,
        field: "year0Qty",
        valueFormatter: (p) => p.value || "",
        ...AgFns.fixWidth(120),
      },
      {
        headerName: `Qty-${currYear - 1}`,
        field: "year1Qty",
        valueFormatter: (p) => p.value || "",
        ...AgFns.fixWidth(120),
      },
      {
        headerName: `Qty-${currYear - 2}`,
        field: "year2Qty",
        valueFormatter: (p) => p.value || "",
        ...AgFns.fixWidth(120),
      },
      {
        headerName: `Qty-${currYear - 3}`,
        field: "year3Qty",
        valueFormatter: (p) => p.value || "",
        ...AgFns.fixWidth(120),
      },
      {
        headerName: `Qty-Inventory`,
        field: "inventoryQty",
        valueFormatter: (p) => p.value || "",
        ...AgFns.fixWidth(120),
      },
      {
        headerName: `Pct-${currYear}-YTD`,
        field: "year0Pct",
        valueFormatter: (p) => UtilFns.fmtPct(p.value, 2),
        ...AgFns.fixWidth(160),
      },
      {
        headerName: `Pct-${currYear - 1}`,
        field: "year1Pct",
        valueFormatter: (p) => UtilFns.fmtPct(p.value, 2),
        ...AgFns.fixWidth(120),
      },
      {
        headerName: `Pct-${currYear - 2}`,
        field: "year2Pct",
        valueFormatter: (p) => UtilFns.fmtPct(p.value, 2),
        ...AgFns.fixWidth(120),
      },
      {
        headerName: `Pct-${currYear - 3}`,
        field: "year3Pct",
        valueFormatter: (p) => UtilFns.fmtPct(p.value, 2),
        ...AgFns.fixWidth(120),
      },
      {
        headerName: "Qty Projected",
        field: "projQty",
        type: "numericColumn",
        editable: true,
        cellClass: "cell-editable",
        valueParser: (p) => this.parseIntOr0(p.newValue),
        ...AgFns.fixWidth(120),
      },
      {
        headerName: "Calc EOQ",
        field: "calcEOQQty",
        type: "numericColumn",
        valueFormatter: (p) => this.fmtNumberOrMproding(p),
        ...AgFns.fixWidth(120),
      },
      {
        headerName: "Safety Stock",
        field: "calcSafetyStockQty",
        type: "numericColumn",
        valueFormatter: (p) => this.fmtNumberOrMproding(p),
        ...AgFns.fixWidth(120),
      },
      {
        headerName: "Reorder Point",
        field: "calcReorderPointQty",
        type: "numericColumn",
        valueFormatter: (p) => this.fmtNumberOrMproding(p),
        ...AgFns.fixWidth(120),
      },
      {
        headerName: "Reorder Period (days)",
        field: "calcReorderPeriod",
        type: "numericColumn",
        valueFormatter: (p) => this.fmtNumberOrMproding(p),
        ...AgFns.fixWidth(120),
      },
    ];
    const sortModel = [{ colId: "eOQSize", sort: "asc" }] as ISortModel;

    AgFns.initGrid(event, colDefs, sortModel);
    this.showHideProdColumns();
  }

  parseIntOr0(value: any) {
    const v = parseInt(value);
    return isNaN(v) ? 0 : v;
  }

  fmtNumberOrMproding(p: any) {
    const value = p.value;
    if (value == null) {
      return "";
    }
    return value;
  }

  updateDatasource() {
    const gridApi = this.ptGridOptions.apiX;
    if (gridApi == null) {
      return;
    }

    const opts = {
      initFn: () => {
        // clear bottom grid after change in top grid.
        this.ptaItems = [];
        this.setSummaryValues(this.ptaGridOptions, []);

        this.prodItems = [];
        this.setSummaryValues(this.prodGridOptions, []);
      },
    };
    const ds = AgFns.buildDatasource(() => this.dbQueryService.createProductTypesForEOQQuery(), null, opts);

    gridApi.setGridOption("serverSideDatasource", ds);

    AgFns.applyGridRouteParams(this.ptGridOptions);
  }

  onFilterChanged() {
    this.updateLocation();
  }

  updateLocation(key: any = null) {
    // Use current path instead of hardcoded path to support both standalone and managed routes
    const currentPath = this.domainService.location.path().split("?")[0];
    const urlTree = this.router.createUrlTree([currentPath]);
    const url = AgFns.buildGridRouteParamsUrl(urlTree, this.ptGridOptions, key && key.toString());
    this.domainService.location.replaceState(url);
  }

  async onPtRowSelected(e: RowSelectedEvent) {
    // check if a deselect event and ignore
    if (!e.node.isSelected()) {
      return;
    }

    const pt = e.data as ProductType;
    if (!pt) {
      return;
    }
    this.currentProductType = pt;
    this.updateLocation(pt.id);
    this.updateShowFormulaEnabled();

    // narrow products to just those that are reorder products or do not have a reorder product
    if (pt.allowReorderFeatureChoices) {
      const reorderProducts = pt.products.map((p) => p.getReorderProduct());
      this.currentProductsForEOQ = _.uniq(reorderProducts);
    } else {
      this.currentProductsForEOQ = pt.products;
    }
    await this.AgFns.busyGrid([this.ptaGridOptions, this.prodGridOptions], this, async () => {
      await this.getProductTypeAccountSummaries(pt.id);
      await this.getProductSummaries(pt.id);
    });
  }

  async editStyle(pt: ProductType) {
    if (!this.user.isInventoryAdmin) {
      await this.dialogService.showOkMessage("Unable to Continue", "Inventory Admin. rights are required for this action");
      return;
    }
    this.updateLocation(pt.id);
    this.router.navigate(["/references/manage/product-type", pt.id]);
  }

  setSummaryValues(gridOptions: GridOptionsExt, summaryValues: any[]) {
    if (gridOptions == this.prodGridOptions) {
      this.prodTotals = summaryValues;
    } else if (gridOptions == this.ptaGridOptions) {
      this.ptaTotals = summaryValues;
    }
    gridOptions.apiX.setGridOption("pinnedBottomRowData", summaryValues);
    AgFns.autoSizeAllColumns(gridOptions);
  }

  async getProductTypeAccountSummaries(productTypeId: number) {
    const q = this.dbQueryService.createProductTypeAccountSummaryQuery(productTypeId);
    const r = await q.execute();
    const summaries = r as unknown as ProductTypeAccountSummary[];
    const { crossTabSummaries, summaryObj } = this.createCrosstabSummaries(summaries, (s) => {
      return { productTypeId: s.productTypeId, accountId: s.accountId };
    });
    this.ptaItems = crossTabSummaries as ProductTypeAccountCrosstabSummary[];
    this.setSummaryValues(this.ptaGridOptions, [summaryObj]);
  }

  oldSummaries: any[] = [];

  async getProductSummaries(productTypeId: number) {
    const q = this.dbQueryService.createProductSummaryQuery(productTypeId);
    const r = await q.execute();
    const summaries = r as unknown as ProductSummary[];
    this.oldSummaries.push(summaries as any);
    // const { crossTabSummaries, summaryObj } = this.createCrosstabSummaries(summaries, (s) => { return { reorderProductId: s.product.getReorderProduct().id } });
    const { crossTabSummaries, summaryObj } = this.createCrosstabSummaries(summaries, (s) => {
      return { reorderProductId: s.product.getReorderProduct().id };
    });
    if (this.currentProductType.eOQTypeId == EOQTypeCode.None) {
      this.prodItems = crossTabSummaries as ProductCrosstabSummary[];
    } else {
      this.prodItems = await this.updateProductEOQRefs(productTypeId, crossTabSummaries);
    }
    this.prodItems = _.sortBy(this.prodItems, (p) => p.product.getSortOrder());
    AgFns.refreshGrid(this.prodGridOptions, this.prodItems);

    this.updatePctSummaryValues(summaryObj);
    this.setSummaryValues(this.prodGridOptions, [summaryObj]);

    this.showHideProdColumns();

    // needed to insure that projQty total gets populated
    await UtilFns.wait(0);
    this.onProdCellValueChanged(null);
  }

  private createCrosstabSummaries<T extends ProductTypeXXXSummary>(ptaSummaries: T[], keyFn: (ptaSummary: T) => object) {
    const currYear = new Date().getFullYear();
    const summaryObj = {
      year0Qty: 0,
      year1Qty: 0,
      year2Qty: 0,
      year3Qty: 0,
      inventoryQty: 0,
    } as any;
    const temp = ptaSummaries.reduce<any>((acc, ptaSummary) => {
      const key = JSON.stringify(keyFn(ptaSummary));
      const { qty, invoiceYear, ...rest } = ptaSummary;
      acc[key] = acc[key] || {
        ...rest,
        year0Qty: 0,
        year1Qty: 0,
        year2Qty: 0,
        year3Qty: 0,
        inventoryQty: 0,
      };
      const item = acc[key];
      const yearKey =
        ptaSummary.invoiceYear == -1 || ptaSummary.invoiceYear == null ? "inventoryQty" : `year${currYear - ptaSummary.invoiceYear}Qty`;
      //const yearKey = (ptaSummary.invoiceYear == -1 ) ? 'inventoryQty' : `year${currYear - ptaSummary.invoiceYear}Qty`;
      item[yearKey] = (item[yearKey] || 0) + ptaSummary.qty;
      summaryObj[yearKey] = (summaryObj[yearKey] || 0) + ptaSummary.qty;
      return acc;
    }, {} as any);
    const crossTabSummaries = Object.values(temp) as ProductTypeXXXCrosstabSummary[];
    return { crossTabSummaries, summaryObj };
  }

  private async updateProductEOQRefs(productTypeId: number, crossTabSummaries: ProductTypeXXXCrosstabSummary[]) {
    const prodEOQs = await this.dbQueryService.getProductEOQs(productTypeId);
    const prodEOQMap = new Map<number, ProductEOQ>();
    prodEOQs.forEach((prodEoq) => prodEOQMap.set(prodEoq.productId, prodEoq));
    // match each crossTabItem ( corresponding to an product) to an ProductTypeEOQ or create one if it doesn't exist.
    let entitiesToSave = [];
    const prodItems = (crossTabSummaries as ProductCrosstabSummary[])
      .map((prodSummary) => {
        // we need an actual instance of the ProductCrosstabSummary class in order for instance methods to work.
        const prodCts = new ProductCrosstabSummary();
        Object.assign(prodCts, prodSummary);
        let prodEoq = prodEOQMap.get(prodCts.productId);
        if (prodEoq == null) {
          const prodForEOQ = this.currentProductsForEOQ.find((x) => x.id == prodSummary.productId);
          if (prodForEOQ == null) {
            return null; // This shouldn't happen but we want to ignore this value if it does.
          }
          const eoqTemplate = <ProductEOQ>{
            productId: prodForEOQ.id,
            projectedUsageQty: 0,
          };
          prodEoq = this.uow.createEntity(ProductEOQ, eoqTemplate, EntityState.Added);
          entitiesToSave.push(prodEoq);
        }
        prodCts.productEOQ = prodEoq;
        return prodCts;
      })
      .filter((x) => x != null);

    if (entitiesToSave.length > 0) {
      await this.dbSaveService.saveSelectedChanges(entitiesToSave);
    }
    return prodItems;
  }

  private updatePctSummaryValues(summaryObj: ProductCrosstabSummary) {
    summaryObj.projQty = 0;
    let calcEOQQty = 0;
    this.prodItems.forEach((item) => {
      item["year0Pct"] = item.year0Qty / summaryObj.year0Qty;
      item["year1Pct"] = item.year1Qty / summaryObj.year1Qty;
      item["year2Pct"] = item.year2Qty / summaryObj.year2Qty;
      item["year3Pct"] = item.year3Qty / summaryObj.year3Qty;
      summaryObj.projQty += item.projQty ?? 0;
      calcEOQQty += this.parseIntOr0(item.calcEOQQty);
    });
    summaryObj.calcEOQQty = calcEOQQty;
    summaryObj["year0Pct"] = summaryObj.year0Qty ? 1 : "";
    summaryObj["year1Pct"] = summaryObj.year1Qty ? 1 : "";
    summaryObj["year2Pct"] = summaryObj.year2Qty ? 1 : "";
    summaryObj["year3Pct"] = summaryObj.year3Qty ? 1 : "";
  }

  getPtaRowStyle(params) {
    if (params.node.rowPinned) {
      return { "font-weight": "bold" };
    }
  }

  getPtaRowId(params: GetRowIdParams) {
    const s = params.data as ProductTypeAccountCrosstabSummary;
    return JSON.stringify({
      productTypeId: s?.productTypeId,
      accountId: s?.accountId,
    });
  }

  getProdRowId(params: GetRowIdParams) {
    const s = params.data as ProductCrosstabSummary;
    return s.productId?.toString() ?? "";
  }

  async onProdCellValueChanged(event) {
    if (event != null) {
      if (event.oldValue != null || event.newValue != null) {
        this.currentProductType.eOQModTs = new Date(Date.now());
        this.ptGridOptions.apiX.redrawRows();
      }
    }

    if (event?.column?.colId == "eOQTypeId") {
      if (event.newValue != EOQTypeCode.None) {
        await this.getProductSummaries(this.currentProductType.id);
        this.prodGridOptions.apiX.redrawRows();
        AgFns.autoSizeAllColumns(this.prodGridOptions);
      }
      this.showHideProdColumns();
      this.updateShowFormulaEnabled();
    }

    // Note: this is subtle - but this stopEditing is needed because otherwise if you
    // are editting the last cell in the grid before the footer and hit tab you will be
    // 'editing' the totals cell - when in editmode - changes to the cell via binding
    // are ignored - so total calcs will be ignored in this case.
    const cell = this.prodGridOptions.apiX.getFocusedCell();
    if (cell?.rowPinned) {
      this.prodGridOptions.apiX.stopEditing();
    }

    this.calcProjQtyColumn(event && event.rowPinned && event.newValue);
    this.calcEOQColumns();
  }

  private async calcProjQtyColumn(valueToProrate: any) {
    if (this.prodItems.length == 0) {
      return;
    }
    let total = 0;

    const gridApi = this.prodGridOptions.apiX;

    gridApi.forEachNode((n) => {
      total += this.parseIntOr0(n.data?.projQty);
    });
    if (valueToProrate != null) {
      if (total == 0) {
        await this.dialogService.showOkMessage("Unabled to prorate", "Unable to prorate based on existing values because they are all 0.");
        return;
      }
      const newTotal = this.parseIntOr0(valueToProrate);
      let cumTotal = 0;
      // prorate value across all the cells
      let maxProd: ProductCrosstabSummary = null;
      this.prodItems.forEach((prod) => {
        const v = prod.projQty ?? 0;
        if (maxProd == null || maxProd.projQty < v) {
          maxProd = prod;
        }
        prod.projQty = Math.round(newTotal * (v / total));
        cumTotal += prod.projQty ?? 0;
      });

      maxProd.projQty += newTotal - cumTotal;
      this.refreshUIForProdItems();
    } else {
      // add up all the values
      const summary = this.prodTotals[0];
      summary.projQty = total;
      this.setSummaryValues(this.prodGridOptions, [summary]);
    }
  }

  calcEOQColumns() {
    if (this.prodTotals.length == 0) {
      return;
    }
    if (this.currentProductType == null) {
      return;
    }
    if (this.currentProductType.eOQTypeId == EOQTypeCode.None) {
      return;
    }
    this.updateDefaultProductTypeValues();
    let sumEOQQty = 0;
    const errorStrings = this.checkForMprodingData();
    this.prodItems.forEach((prod) => {
      prod.recalcAll();
      sumEOQQty += prod.calcEOQQty;
    });

    this.refreshUIForProdItems();

    const summary = this.prodTotals[0];
    summary.calcEOQQty = sumEOQQty;
    this.setSummaryValues(this.prodGridOptions, [summary]);
  }

  private refreshUIForProdItems() {
    this.prodItems = this.prodItems.slice();
    // Hack - this is needed because ag-grid does not do change detection on getters automatically - this forces it.
    this.prodGridOptions.apiX.refreshCells({ force: true });
  }

  checkForMprodingData() {
    const pt = this.currentProductType;
    const errorStrings: string[] = [];
    if (pt.orderingCostAmt == null) {
      errorStrings.push("missing ordering cost");
    }
    if (pt.unitCarryingCostPerYearAmt == null) {
      errorStrings.push("missing carrying cost");
    }
    if (pt.safetyStockPerYearPct == null) {
      errorStrings.push("missing safety stock pct");
    }
  }

  private updateDefaultProductTypeValues() {
    const pt = this.currentProductType;
    if (pt.eOQTypeId == EOQTypeCode.None) {
      return;
    }
    let shouldRefreshPtGrid = false;

    if (pt.leadTimeDays == null) {
      pt.leadTimeDays = pt.manufacturer.leadTimeDays;
      shouldRefreshPtGrid = true;
    }
    const appConfig = this.dbQueryService.appConfig;
    if (pt.orderingCostAmt == null) {
      pt.orderingCostAmt = appConfig.productTypeDefault.orderingCostAmt;
      shouldRefreshPtGrid = true;
    }

    if (pt.safetyStockPerYearPct == null) {
      pt.safetyStockPerYearPct = appConfig.productTypeDefault.safetyStockPerYearPct;
      shouldRefreshPtGrid = true;
    }

    if (pt.unitCarryingCostPerYearAmt == null) {
      pt.unitCarryingCostPerYearAmt = appConfig.productTypeDefault.unitCarryingCostPerYearAmt;
      shouldRefreshPtGrid = true;
    }

    if (shouldRefreshPtGrid) {
      this.ptGridOptions.apiX.redrawRows();
    }
  }

  showHideProdColumns() {
    const gridApi = this.prodGridOptions?.apiX;
    if (gridApi == null) {
      return;
    }
    const qtyFields = ["year0Qty", "year1Qty", "year2Qty", "year3Qty", "inventoryQty"];
    const pctFields = ["year0Pct", "year1Pct", "year2Pct", "year3Pct"];
    const eoqFields = ["projQty", "calcEOQQty", "calcSafetyStockQty", "calcReorderPointQty"];
    gridApi.setColumnsVisible(qtyFields, !this.showPercentages);
    gridApi.setColumnsVisible(pctFields, this.showPercentages);
    gridApi.setColumnsVisible(eoqFields, this.currentProductType?.eOQTypeId != EOQTypeCode.None);
    gridApi.setColumnsVisible(["calcSafetyStockQty", "calcReorderPeriod"], this.currentProductType?.eOQTypeId == EOQTypeCode.FixedTime);
    gridApi.setColumnsVisible(["calcReorderPointQty"], this.currentProductType?.eOQTypeId == EOQTypeCode.FixedQuantity);
  }

  canShowFormula() {
    const eOQTypeId = this.currentProductType?.eOQTypeId;
    return eOQTypeId == EOQTypeCode.FixedTime || eOQTypeId == EOQTypeCode.FixedQuantity;
  }

  updateShowFormulaEnabled() {
    this.inventoryManager?.updateShowFormulaEnabled(this.canShowFormula());
  }

  async showFormula() {
    const eOQTypeId = this.currentProductType?.eOQTypeId;
    if (eOQTypeId == EOQTypeCode.FixedQuantity) {
      await this.dialogService.showOkMessage(
        "Fixed Quantity",
        `EOQ = SQRT((2 * ProjectedAnnualQuantity * OrderingCostPerOrder)/UnitCarryingCostPerYear))<br>
        ReorderPoint = Round( ((ProjectedAnnualQuantity/250) * LeadTimeInDays) + (SafetyStockPctPerYear * ProjectedAnnualQuantity) )<br> )
           (Note: 250 is the approximate number of workdays in a normal year)`
      );
    } else if (eOQTypeId == EOQTypeCode.FixedTime) {
      await this.dialogService.showOkMessage(
        "Fixed Time",
        `EOQ = SQRT((2 * (ProjectedAnnualQuantity * (1 + SafetyStockPctPerYear)) * OrderingCostPerOrder)/UnitCarryingCostPerYear))<br>
        SafetyStock = Round(ProjectedAnnualQuantity * SafetyStockPctPerYear)`
      );
    }
  }

  async cancelChanges() {
    this.stopEditing();
    this.uow.rollback();
    this.ptGridOptions.apiX.redrawRows();
    this.showHideProdColumns();
    this.onProdCellValueChanged(null);
  }

  async saveChanges() {
    this.stopEditing();
    const sr = await this.dbSaveService.saveChanges();
    this.toastr.success("Inventory EOQ saved");
  }

  stopEditing() {
    this.ptGridOptions.apiX.stopEditing();
    this.prodGridOptions.apiX.stopEditing();
  }

  goProductTypes() {
    this.router.navigate(["/references/manage/product-types"]);
  }

  // calcEOQ(prod: ProductCrosstabSummary) {
  //   const pt = this.currentProductType;

  //   const qty = prod.projQty;
  //   if (qty ==  0) {
  //     prod.calcEOQQty = 0;
  //   } else if (pt.eOQTypeId == EOQTypeCode.FixedQuantity) {
  //     const base = (2 * qty * pt.orderingCostAmt)/pt.unitCarryingCostPerYearAmt;
  //     prod.calcEOQQty = Math.round(Math.sqrt(base));
  //   } else if (pt.eOQTypeId == EOQTypeCode.FixedTime) {
  //     const safetyStock =  qty * (pt.safetyStockPerYearPct);
  //     const base = (2 * ( qty + safetyStock) * pt.orderingCostAmt)/pt.unitCarryingCostPerYearAmt;
  //     prod.calcEOQQty = Math.round(Math.sqrt(base));
  //   } else {

  //   }

  // }

  // calcSafetyStock(prod: ProductCrosstabSummary) {
  //   const pt = this.currentProductType;
  //   const qty = prod.projQty;
  //   if (qty == 0) {
  //     prod.calcSafetyStockQty = 0;
  //   } else {
  //     prod.calcSafetyStockQty = Math.round(qty * pt.safetyStockPerYearPct);
  //   }
  // }

  // calcReorderPoint(prod: ProductCrosstabSummary) {
  //   const pt = this.currentProductType;
  //   const eoqQty = prod.calcEOQQty;
  //   if (eoqQty == 0) {
  //     prod.calcReorderPointQty = 0;
  //   } else {
  //     prod.calcReorderPointQty = Math.round(((eoqQty/250)*(pt.leadTimeDays ?? 0)) + (pt.safetyStockPerYearPct * eoqQty));
  //   }
  // }

  // calcReorderPeriod(prod: ProductCrosstabSummary) {
  //   const pt = this.currentProductType;
  //   const eoqQty = prod.calcEOQQty;
  //   if (eoqQty == 0) {
  //     prod.calcReorderPeriod = 0;
  //   } else {
  //     prod.calcReorderPeriod =  Math.round( 365 * eoqQty / prod.projQty);
  //   }
  // }
}
